import React, { memo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ComposableMap, Geographies, Geography, Marker, Annotation } from 'react-simple-maps';
import { STATE_NAME_TO_CODE, STATE_ZONES, getZoneInfo, ORIGIN } from '../data/zones';
import { slugifyState } from '../data/states';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';

function USMap({ highlightCode, interactive = true }) {
  const navigate = useNavigate();
  const [tooltip, setTooltip] = useState(null);

  const handleMove = (e) => {
    if (!tooltip) return;
    setTooltip((t) => (t ? { ...t, x: e.clientX, y: e.clientY } : t));
  };

  return (
    <div className="relative w-full" onMouseMove={handleMove} onMouseLeave={() => setTooltip(null)}>
      <ComposableMap
        projection="geoAlbersUsa"
        projectionConfig={{ scale: 1000 }}
        width={980}
        height={580}
        style={{ width: '100%', height: 'auto' }}
      >
        <Geographies geography={GEO_URL}>
          {({ geographies }) =>
            geographies.map((geo) => {
              const stateName = geo.properties.name;
              const code = STATE_NAME_TO_CODE[stateName];
              const zone = STATE_ZONES[code];
              const info = getZoneInfo(zone);
              const isHighlight = highlightCode && code === highlightCode;
              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill={info.color}
                  stroke="#ffffff"
                  strokeWidth={isHighlight ? 2 : 0.7}
                  onMouseEnter={(e) => {
                    if (!code || !zone) return;
                    setTooltip({
                      stateName,
                      code,
                      zone,
                      label: info.label,
                      color: info.color,
                      x: e.clientX,
                      y: e.clientY,
                    });
                  }}
                  onMouseLeave={() => setTooltip(null)}
                  onClick={
                    interactive
                      ? () => {
                          if (!stateName) return;
                          setTooltip(null);
                          navigate(`/zones/${slugifyState(stateName)}`);
                        }
                      : undefined
                  }
                  style={{
                    default: {
                      outline: 'none',
                      filter: isHighlight ? 'brightness(1.05)' : 'none',
                    },
                    hover: {
                      outline: 'none',
                      filter: 'brightness(1.12)',
                      cursor: interactive ? 'pointer' : 'default',
                    },
                    pressed: { outline: 'none' },
                  }}
                />
              );
            })
          }
        </Geographies>

        {/* Origin marker — Weston, FL */}
        <Marker coordinates={ORIGIN.coordinates}>
          <g style={{ pointerEvents: 'none' }}>
            <circle r={9} fill="#4f46e5" stroke="#ffffff" strokeWidth={3} />
            <circle r={3.5} fill="#ffffff" />
          </g>
        </Marker>

        <Annotation
          subject={ORIGIN.coordinates}
          dx={28}
          dy={-22}
          connectorProps={{
            stroke: '#1e293b',
            strokeWidth: 1.3,
            strokeLinecap: 'round',
          }}
        >
          <text
            x={4}
            y={2}
            fontSize={13}
            fontWeight={700}
            fill="#0f172a"
            style={{ paintOrder: 'stroke', stroke: '#ffffff', strokeWidth: 3 }}
          >
            {ORIGIN.city}
          </text>
        </Annotation>
      </ComposableMap>

      {tooltip && (
        <div
          className="pointer-events-none fixed z-50 px-3 py-2 rounded-lg bg-slate-900 text-white shadow-xl border border-slate-700 min-w-[180px]"
          style={{
            left: tooltip.x + 14,
            top: tooltip.y + 14,
            transform: 'translateZ(0)',
          }}
          role="tooltip"
        >
          <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">
            {tooltip.stateName}
          </p>
          <p className="mt-1 flex items-center gap-2">
            <span
              className="inline-block w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: tooltip.color }}
            />
            <span className="text-sm font-bold">
              Zone {tooltip.zone}
            </span>
            <span className="text-sm font-medium text-slate-300">— {tooltip.label}</span>
          </p>
        </div>
      )}
    </div>
  );
}

export default memo(USMap);
